import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LigationCalculator = () => {
  const [ligationData, setLigationData] = useState({
    insertConcentration: '',
    insertLength: '',
    vectorConcentration: '',
    vectorLength: '',
    insertToVectorRatio: '3',
    totalReactionVolume: '20',
    enzymeConcentration: '',
    showDetailedAnalysis: false
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLigationData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCheckboxChange = (e) => {
    setLigationData(prev => ({
      ...prev,
      showDetailedAnalysis: e.target.checked
    }));
  };

  const calculateLigation = () => {
    try {
      // Your calculation logic here
      const calculatedResults = {
        insertVolume: '0',
        vectorVolume: '0',
        bufferVolume: '0',
        enzymeVolume: '0',
        efficiency: '0',
        optimizationTips: []
      };
      
      setResults(calculatedResults);
      setError('');
    } catch (err) {
      setError('Error calculating ligation parameters');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="min-h-screen bg-gray-50"
    >
      <Helmet>
        <title>Ligation Calculator</title>
        <meta name="description" content="Calculate optimal ligation conditions for molecular biology experiments" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Ligation Calculator</h1>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8  max-w-2xl mx-auto">
            <div className="calculator-header flex items-center mb-8">
              <div className="header-icon text-4xl text-purple-600 mr-4">🧬</div>
              <h1 className="text-3xl font-bold text-gray-800">DNA Ligation Calculator</h1>
            </div>

            <div className="text-gray-600 mb-8">
              <p>The DNA Ligation Calculator helps you optimize DNA ligation reactions and provides:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Volume calculations</li>
                <li>Efficiency estimation</li>
                <li>Optimization recommendations</li>
                <li>Protocol optimization</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-6">
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Insert DNA Concentration (ng/µL):</label>
                <input
                  type="number"
                  name="insertConcentration"
                  value={ligationData.insertConcentration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter insert DNA concentration"
                />
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Insert DNA Length (bp):</label>
                <input
                  type="number"
                  name="insertLength"
                  value={ligationData.insertLength}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter insert DNA length"
                />
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vector DNA Concentration (ng/µL):</label>
                <input
                  type="number"
                  name="vectorConcentration"
                  value={ligationData.vectorConcentration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter vector DNA concentration"
                />
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Vector DNA Length (bp):</label>
                <input
                  type="number"
                  name="vectorLength"
                  value={ligationData.vectorLength}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter vector DNA length"
                />
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Insert to Vector Ratio:</label>
                <input
                  type="number"
                  name="insertToVectorRatio"
                  value={ligationData.insertToVectorRatio}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter insert to vector ratio"
                />
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Total Reaction Volume (µL):</label>
                <input
                  type="number"
                  name="totalReactionVolume"
                  value={ligationData.totalReactionVolume}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter total reaction volume"
                />
              </div>

              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Enzyme Concentration (U/µL):</label>
                <input
                  type="number"
                  name="enzymeConcentration"
                  value={ligationData.enzymeConcentration}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter enzyme concentration (optional)"
                />
              </div>

              <div className="input-group">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="showDetailedAnalysis"
                    checked={ligationData.showDetailedAnalysis}
                    onChange={handleCheckboxChange}
                    className="form-checkbox h-5 w-5 text-purple-600 mr-2"
                  />
                  <span className="text-gray-700">Show detailed optimization analysis and tips</span>
                </label>
              </div>
            </div>

            <button
              onClick={calculateLigation}
              className="w-full py-3 px-4 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors duration-200"
            >
              Calculate Ligation Parameters
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Ligation Reaction Parameters</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Insert Volume</h3>
                    <p className="text-xl font-bold">{results.insertVolume} µL</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Vector Volume</h3>
                    <p className="text-xl font-bold">{results.vectorVolume} µL</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Buffer Volume</h3>
                    <p className="text-xl font-bold">{results.bufferVolume} µL</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Enzyme Volume</h3>
                    <p className="text-xl font-bold">{results.enzymeVolume} µL</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Estimated Efficiency</h3>
                    <p className="text-xl font-bold">{results.efficiency}%</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Ligation Efficiency (%)'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Insert to Vector Ratio'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Optimal Insert to Vector Ratio'
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Optimization Tips</h3>
                  <ul className="list-disc pl-6 space-y-2">
                    {results.optimizationTips.map((tip, index) => (
                      <li key={index} className="text-gray-600">{tip}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LigationCalculator;